import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import { useRouteLoaderData } from 'react-router'

import appDefaults from '~/portal/portal.defaults.json'
import { AppLoaderData } from '~/portal/types/app.ts'
import { useAppConfig } from '~/shared/hooks/useAppConfig'
import sharedDefaults from '~/shared/shared.defaults.json'
import { PORTAL_LICENSE_TYPE } from '~/shared/types/license'
import { PlanExpirationLevel } from '~/shared/types/plan'
import { ApiHubPlan } from '~/shared/types/portal'
import { SWAGGERHUB_ROLE } from '~/shared/types/shub'
import { replacePlaceholders } from '~/shared/utils/string'
import { getPlanExpirationLevel, getTrialDaysLeft } from '~/shared/utils/trial'

export type UseApiHubPlanResult = Partial<ApiHubPlan> & {
  getUpgradeUrl: (role?: SWAGGERHUB_ROLE) => string,
  getUpgradeLabel: (role?: SWAGGERHUB_ROLE) => string,
  daysLeft: number,
  expirationLevel: PlanExpirationLevel,
  trial: boolean,
  daysAfterCutover: number,
  templatesEnabled: boolean,
  isExternalLoginEnabled?: boolean,
  hasActiveRenewal: boolean,
}

export const UNLIMITED = 2147483647

export const useApiHubPlan = (): UseApiHubPlanResult => {
  const { t } = useTranslation()

  const {
    apiHubPlan,
    swaggerhubOrgId,
    license,
    cutoverDate,
    platformAdmin,
    isExternalLoginEnabled,
  } = useRouteLoaderData<AppLoaderData>('root') || {}

  const {
    platformManaged,
    subscriptionId,
    subscriptionAuthority,
    expiresAt,
    renewalDate,
    templateMeters,
  } = apiHubPlan || {}

  const { platformUiUrl } = useAppConfig()

  const trial = license?.type === PORTAL_LICENSE_TYPE.TRIAL

  const daysLeft = expiresAt ? getTrialDaysLeft(expiresAt) : UNLIMITED
  const expirationLevel = getPlanExpirationLevel(daysLeft)

  const getUpgradeUrl = useCallback((role?: SWAGGERHUB_ROLE) => {
    if (!platformManaged) {
      return appDefaults.contactUrl
    }

    const { membersUrl, subscriptionsUrl, modifySubscriptionUrl } = sharedDefaults.platform

    if (role !== SWAGGERHUB_ROLE.OWNER && !platformAdmin) {
      return replacePlaceholders(`${platformUiUrl}${membersUrl}`, { organizationId: swaggerhubOrgId })
    }

    return subscriptionId
      ? replacePlaceholders(`${platformUiUrl}${modifySubscriptionUrl}`, { organizationId: swaggerhubOrgId, subscriptionId })
      : replacePlaceholders(`${platformUiUrl}${subscriptionsUrl}`, { organizationId: swaggerhubOrgId })
  }, [trial, swaggerhubOrgId, platformManaged, subscriptionAuthority, subscriptionId])

  const getUpgradeLabel = useCallback((role?: SWAGGERHUB_ROLE) => {
    if (!platformManaged) {
      return t('entitlements.contactUs')
    }
    return (role === SWAGGERHUB_ROLE.OWNER || platformAdmin)
      ? trial ? t('entitlements.managePlanLink') : t('entitlements.upgradeLink.admin')
      : t('entitlements.upgradeLink.user')
  }, [trial, platformManaged])

  const getDaysAfterCutover = (cutoverDate: string): number => {
    const daysExpired = Math.round((new Date().getTime() - new Date(cutoverDate).getTime()) / (1000 * 3600 * 24))
    return daysExpired > 0 ? daysExpired : 0
  }

  const daysAfterCutover = cutoverDate ? getDaysAfterCutover(cutoverDate) : -1

  const templatesEnabled = !!templateMeters?.limit && templateMeters.limit > 0

  const hasActiveRenewal = !!renewalDate

  return {
    ...apiHubPlan,
    trial,
    getUpgradeUrl,
    getUpgradeLabel,
    daysLeft,
    expirationLevel,
    daysAfterCutover,
    templatesEnabled,
    isExternalLoginEnabled,
    hasActiveRenewal,
  }
}